<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InvoiceEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    invoice: {
      type: Object,
      required: false,
    },
    target: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const formId = ref(null);
  const defaultTitle = ref($fn.value.pgettext('Invoice', 'Edit Invoice'));
  const targetConfig = ref({
    header: {
      title: $fn.value.pgettext('Invoice', 'Edit Invoice Header'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'company'],
    },
  });
  const editor = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    formId: formId.value,
    defaultTitle: defaultTitle.value,
    targetConfig: targetConfig.value,
    editor: editor.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_editor = async () => {
    return props.invoice.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'formId' },
      { name: 'defaultTitle' },
      { name: 'targetConfig' },
      { name: 'editor' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId, editor: $$load_editor },
    ux: {},
    dataRefs: {
      labelAlign,
      formId,
      defaultTitle,
      targetConfig,
      editor,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.target
          ? targetConfig.value[props.target].title
          : defaultTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.invoice,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('invoice');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_s3zegwnqa_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_fkxfcvzi3_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_1040167445267876_crud_nghijfwi2_getActionArgs = async ($event) => {
    return {
      objectType: 'invoice',
      op: 'upsert',
      data: {
        ...editor.value.getChanges({ skipEmbeddedLists: true }),
        id: editor.value.id,
        etag: editor.value.etag,
      },
    };
  };
  const act_2434514588044777_closeModal_w1kvstsml_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_s3zegwnqa =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_s3zegwnqa_getActionArgs,
    });
  const inst_evh_7315092382398562_close_wbesz2n0u =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_s3zegwnqa,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_wbesz2n0u],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_8rt83sart =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_75xznfqxw =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_8rt83sart,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_75xznfqxw],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_4lmj7lug1 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_kc3unvwd8 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_4lmj7lug1,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_kc3unvwd8],
    event: 'close',
  });
  const act_1040167445267876_closeModal_fkxfcvzi3 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_fkxfcvzi3_getActionArgs,
    });
  const inst_evh_1040167445267876_success_vsgjyu65o =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_fkxfcvzi3,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_crud_nghijfwi2 = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_nghijfwi2_getActionArgs,
    displayName: 'save',
    events: [inst_evh_1040167445267876_success_vsgjyu65o],
  });
  const inst_evh_1040167445267876_submit_4msgwz7d7 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_nghijfwi2,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_4msgwz7d7],
    event: 'submit',
  });
  const act_2434514588044777_closeModal_w1kvstsml =
    new actionTypes.CloseModalAction({
      actionArgs: act_2434514588044777_closeModal_w1kvstsml_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_f3xv2oj3g =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeModal_w1kvstsml,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_f3xv2oj3g],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_wbesz2n0u,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_75xznfqxw,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_kc3unvwd8,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_vsgjyu65o,
    inst_evh_1040167445267876_submit_4msgwz7d7,
    save: inst_evh_1040167445267876_submit_4msgwz7d7,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_f3xv2oj3g,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_wbesz2n0u = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_wbesz2n0u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_75xznfqxw = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_75xznfqxw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_kc3unvwd8 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_kc3unvwd8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_vsgjyu65o = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_vsgjyu65o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_4msgwz7d7 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_4msgwz7d7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_f3xv2oj3g = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_f3xv2oj3g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="InvoiceEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="invoice"
        autofocus="first"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetInvoiceHeader
          :formId="formId"
          :invoice="editor"
          :labelAlign="labelAlign"
        >
        </FieldsetInvoiceHeader>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          :disabled="!forms.invoice || !forms.invoice.submittable"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
